'use client';

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { DEFAULT_ACCENT_COLOR } from '@/components/theme';

const STORAGE_LIGHT = 'fsm-accent-light';
const STORAGE_DARK = 'fsm-accent-dark';

const HEX6 = /^#[0-9A-Fa-f]{6}$/;

function readStored(key: string): string | null {
  if (typeof window === 'undefined') return null;
  const raw = window.localStorage.getItem(key);
  return raw && HEX6.test(raw) ? raw.toLowerCase() : null;
}

function normalizeAccent(value: string): string | null {
  const t = value.trim();
  return HEX6.test(t) ? t.toLowerCase() : null;
}

export interface AccentColorsContextType {
  lightAccent: string;
  darkAccent: string;
  setLightAccent: (hex: string) => void;
  setDarkAccent: (hex: string) => void;
  resetLightAccent: () => void;
  resetDarkAccent: () => void;
  resetAllAccents: () => void;
}

const AccentColorsContext = createContext<AccentColorsContextType | undefined>(undefined);

export const useAccentColors = () => {
  const ctx = useContext(AccentColorsContext);
  if (!ctx) {
    throw new Error('useAccentColors must be used within an AccentColorsProvider');
  }
  return ctx;
};

interface AccentColorsProviderProps {
  children: ReactNode;
}

export const AccentColorsProvider: React.FC<AccentColorsProviderProps> = ({ children }) => {
  const [lightAccent, setLightState] = useState(
    () => readStored(STORAGE_LIGHT) ?? DEFAULT_ACCENT_COLOR,
  );
  const [darkAccent, setDarkState] = useState(
    () => readStored(STORAGE_DARK) ?? DEFAULT_ACCENT_COLOR,
  );

  useEffect(() => {
    window.localStorage.setItem(STORAGE_LIGHT, lightAccent);
  }, [lightAccent]);

  useEffect(() => {
    window.localStorage.setItem(STORAGE_DARK, darkAccent);
  }, [darkAccent]);

  const setLightAccent = useCallback((hex: string) => {
    const n = normalizeAccent(hex);
    if (n) setLightState(n);
  }, []);

  const setDarkAccent = useCallback((hex: string) => {
    const n = normalizeAccent(hex);
    if (n) setDarkState(n);
  }, []);

  const resetLightAccent = useCallback(() => setLightState(DEFAULT_ACCENT_COLOR), []);
  const resetDarkAccent = useCallback(() => setDarkState(DEFAULT_ACCENT_COLOR), []);

  const resetAllAccents = useCallback(() => {
    setLightState(DEFAULT_ACCENT_COLOR);
    setDarkState(DEFAULT_ACCENT_COLOR);
  }, []);

  const value = useMemo(
    () => ({
      lightAccent,
      darkAccent,
      setLightAccent,
      setDarkAccent,
      resetLightAccent,
      resetDarkAccent,
      resetAllAccents,
    }),
    [
      lightAccent,
      darkAccent,
      setLightAccent,
      setDarkAccent,
      resetLightAccent,
      resetDarkAccent,
      resetAllAccents,
    ],
  );

  return <AccentColorsContext.Provider value={value}>{children}</AccentColorsContext.Provider>;
};
